import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPlus, FaTrash, FaImage, FaSave, FaArrowLeft } from 'react-icons/fa';
import { getTeacherSubjects } from '../../redux/slices/subjectsSlice';
import { fetchSubjectQuestionBank } from '../../redux/slices/questionBankSlice';
import { toast } from 'react-toastify';
import './CreateDuty.css';

function CreateDuty() {
    const { isDarkMode } = useDarkMode();
    const navigate = useNavigate();
    const { lessonId } = useParams();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.auth);
    const { teacherSubjects, subjectsLoading } = useSelector((state) => state.subjects);
    const { questionBank, loading: questionBankLoading } = useSelector((state) => state.questionBank);
    
    const [activeTab, setActiveTab] = useState('addQuestions');
    const [selectedSubjectId, setSelectedSubjectId] = useState('');
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    // Duty basic info
    const [dutyInfo, setDutyInfo] = useState({
        title: '',
        subject_id: '',
        lesson_id: lessonId || ''
    });
    
    // Added questions (new questions)
    const [addedQuestions, setAddedQuestions] = useState([
        {
            text: '',
            image: null,
            answers: [
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false }
            ]
        }
    ]);
    
    // Selected questions from bank
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    
    // Available subjects for selected class
    const [availableSubjects, setAvailableSubjects] = useState([]);
    
    // Loading state for submit
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Submit duty
    const handleSubmit = async () => {
        // Validate basic info
        if (!dutyInfo.title || !dutyInfo.subject_id || !dutyInfo.lesson_id) {
            toast.error('يرجى ملء جميع البيانات الأساسية للواجب');
            return;
        }
        
        // Check if there are questions
        const hasAddedQuestions = addedQuestions.some(q => q.text.trim() && q.answers.some(a => a.text.trim() && a.true_status));
        const hasSelectedQuestions = selectedQuestions.length > 0;
        
        if (!hasAddedQuestions && !hasSelectedQuestions) {
            toast.error('يرجى إضافة أسئلة أو اختيار أسئلة من بنك الأسئلة');
            return;
        }
        
        try {
            setIsSubmitting(true);
            let requestData = {
                ...dutyInfo,
                subject_id: selectedSubjectId
            };
            
            if (hasAddedQuestions) {
                requestData.added_questions = addedQuestions
                    .filter(q => q.text.trim() && q.answers.some(a => a.text.trim() && a.true_status))
                    .map(q => ({
                        text: q.text,
                        image: q.image,
                        answers: q.answers.map(a => ({
                            text: a.text,
                            image: a.image,
                            true_status: a.true_status
                        }))
                    }));
            }
            
            if (hasSelectedQuestions) {
                requestData.questions = selectedQuestions.map(q => q.question_id);
            }
            
            // API call to create duty
            const token = localStorage.getItem('token3almny');
            const formData = new FormData();
            
            // Add basic info
            formData.append('subject_id', requestData.subject_id);
            formData.append('lesson_id', requestData.lesson_id);
            
            // Add questions from bank
            if (requestData.questions) {
                requestData.questions.forEach((questionId, index) => {
                    formData.append(`questions[${index}]`, questionId);
                });
            }
            
            // Add new questions
            if (requestData.added_questions) {
                requestData.added_questions.forEach((question, qIndex) => {
                    formData.append(`added_questions[${qIndex}][text]`, question.text);
                    if (question.image) {
                        formData.append(`added_questions[${qIndex}][image]`, question.image);
                    }
                    
                    question.answers.forEach((answer, aIndex) => {
                        formData.append(`added_questions[${qIndex}][answers][${aIndex}][text]`, answer.text);
                        formData.append(`added_questions[${qIndex}][answers][${aIndex}][true_status]`, answer.true_status ? "1" : "0");
                        if (answer.image) {
                            formData.append(`added_questions[${qIndex}][answers][${aIndex}][image]`, answer.image);
                        }
                    });
                });
            }
            
            const response = await fetch('https://newalemny.3almny.app/api/lessons/duties/add_lesseon_duty', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });
            
            const result = await response.json();
            
            if (response.ok) {
                toast.success('تم إنشاء الواجب بنجاح!');
                navigate(`/lessonDetails/${lessonId}`);
            } else {
                toast.error(result.message || 'فشل في إنشاء الواجب');
            }
            
        } catch (error) {
            console.error('Error creating duty:', error);
            toast.error('حدث خطأ أثناء إنشاء الواجب');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // Form handlers
    const handleDutyInfoChange = (field, value) => {
        setDutyInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    const addQuestion = () => {
        setAddedQuestions(prev => [...prev, {
            text: '',
            image: null,
            answers: [
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false }
            ]
        }]);
    };
    
    const removeQuestion = (index) => {
        setAddedQuestions(prev => prev.filter((_, i) => i !== index));
    };
    
    const handleQuestionTextChange = (index, value) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === index ? { ...q, text: value } : q
        ));
    };
    
    const handleQuestionImageChange = (index, file) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === index ? { ...q, image: file } : q
        ));
    };
    
    const addAnswer = (questionIndex) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: [...q.answers, { text: '', image: null, true_status: false }]
            } : q
        ));
    };
    
    const removeAnswer = (questionIndex, answerIndex) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: q.answers.filter((_, aIndex) => aIndex !== answerIndex)
            } : q
        ));
    };
    
    const handleAnswerTextChange = (questionIndex, answerIndex, value) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: q.answers.map((a, aIndex) => 
                    aIndex === answerIndex ? { ...a, text: value } : a
                )
            } : q
        ));
    };
    
    const handleAnswerImageChange = (questionIndex, answerIndex, file) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: q.answers.map((a, aIndex) => 
                    aIndex === answerIndex ? { ...a, image: file } : a
                )
            } : q
        ));
    };
    
    const handleCorrectAnswerChange = (questionIndex, answerIndex) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: q.answers.map((a, aIndex) => ({
                    ...a,
                    true_status: aIndex === answerIndex
                }))
            } : q
        ));
    };
    
    const handleQuestionSelection = (questionId) => {
        setSelectedQuestions(prev => {
            const exists = prev.find(q => q.question_id === questionId);
            if (exists) {
                return prev.filter(q => q.question_id !== questionId);
            } else {
                return [...prev, { question_id: questionId }];
            }
        });
    };
    
    const getAnswerLetter = (index) => {
        const letters = ['أ', 'ب', 'ج', 'د', 'ه', 'و', 'ز', 'ح', 'ط', 'ي'];
        return letters[index] || String.fromCharCode(65 + index);
    };
    
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'questionBank' && selectedSubjectId) {
            dispatch(fetchSubjectQuestionBank(selectedSubjectId));
        }
    };
    
    // Check authentication and dispatch getTeacherSubjects
    useEffect(() => {
        const token = localStorage.getItem('token3almny');
        const userRole = JSON.parse(localStorage.getItem('user3almny') || '{}');
        
        if (!token || userRole.role !== 'teacher') {
            navigate('/login');
            return;
        }
        
        // If we have token but no currentUser, wait for Redux to load
        if (token && userRole.role === 'teacher' && !currentUser) {
            console.log('Token exists but no currentUser, waiting for Redux...');
            return;
        }
        
        // If we have both token and currentUser, proceed
        if (token && userRole.role === 'teacher' && currentUser) {
            console.log('User authenticated, dispatching getTeacherSubjects');
            dispatch(getTeacherSubjects());
        }
    }, [dispatch, currentUser, navigate]);
    
    // Watch for currentUser changes and dispatch getTeacherSubjects when available
    useEffect(() => {
        console.log('currentUser changed:', currentUser);
        if (currentUser && currentUser.role === 'teacher') {
            console.log('currentUser loaded, dispatching getTeacherSubjects');
            dispatch(getTeacherSubjects());
        }
    }, [currentUser, dispatch]);
    
    // Debug teacherSubjects loading
    useEffect(() => {
        console.log('teacherSubjects changed:', teacherSubjects);
        console.log('teacherSubjects length:', teacherSubjects?.length);
        console.log('teacherSubjects is array:', Array.isArray(teacherSubjects));
        if (teacherSubjects && teacherSubjects.length > 0) {
            console.log('First subject:', teacherSubjects[0]);
            // Set available subjects when teacherSubjects loads
            setAvailableSubjects(teacherSubjects);
        }
    }, [teacherSubjects]);
    
    // When availableSubjects change, auto-select first subject if none selected
    useEffect(() => {
        if (availableSubjects && availableSubjects.length > 0 && !selectedSubjectId) {
            const firstSubjectId = availableSubjects[0].id;
            console.log('Auto-selecting first subject:', firstSubjectId);
            setSelectedSubjectId(firstSubjectId);
            setDutyInfo(prev => ({ ...prev, subject_id: firstSubjectId }));
        }
    }, [availableSubjects, selectedSubjectId]);
    
    // When subject changes, update dutyInfo
    useEffect(() => {
        console.log('Subject changed to:', selectedSubjectId);
        if (selectedSubjectId) {
            setDutyInfo(prev => ({
                ...prev,
                subject_id: selectedSubjectId
            }));
        }
    }, [selectedSubjectId]);
    
    // Fetch question bank when subject changes
    useEffect(() => {
        if (selectedSubjectId && activeTab === 'questionBank') {
            dispatch(fetchSubjectQuestionBank(selectedSubjectId));
        }
    }, [selectedSubjectId, activeTab, dispatch]);

    if (!currentUser || currentUser.role !== 'teacher') {
        return null;
    }

    return (
        <div className={`create-duty-section ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <div className="container">
                {/* Header */}
                <div className="create-duty-header">
                    <button 
                        className="back-btn"
                        onClick={() => navigate(`/lessonDetails/${lessonId}`)}
                    >
                        <FaArrowLeft />
                        رجوع
                    </button>
                    <h5 style={{marginTop : "-10px"}}>إنشاء واجب جديد</h5>
                </div>
                
                {/* Basic Duty Info */}
                <div className="duty-info-section">
                    <h2>معلومات الواجب الأساسية</h2>
                    <div className="duty-info-grid">
                        <div className="form-group">
                            <label>عنوان الواجب</label>
                            <input
                                type="text"
                                value={dutyInfo.title}
                                onChange={(e) => handleDutyInfoChange('title', e.target.value)}
                                placeholder="أدخل عنوان الواجب"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>المادة الدراسية</label>
                            <select
                                value={selectedSubjectId}
                                onChange={(e) => {
                                    setSelectedSubjectId(e.target.value);
                                    setDutyInfo(prev => ({ ...prev, subject_id: e.target.value }));
                                }}
                                disabled={subjectsLoading}
                            >
                                <option value="">اختر المادة</option>
                                {availableSubjects.map(subject => (
                                    <option key={subject.id} value={subject.id}>
                                        {subject.title}
                                    </option>
                                ))}
                            </select>
                            {subjectsLoading && <div className="loading-text">جاري تحميل المواد...</div>}
                        </div>
                    </div>
                </div>
                
                                {/* Tabs */}
                <div className="tabs-container">
                    <div className="tabs-header">
                        <button
                            className={`tab-button ${activeTab === 'addQuestions' ? 'active' : ''}`}
                            onClick={() => handleTabChange('addQuestions')}
                        >
                            إضافة أسئلة جديدة
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'questionBank' ? 'active' : ''}`}
                            onClick={() => handleTabChange('questionBank')}
                        >
                            الاختيار من بنك الأسئلة
                        </button>
                    </div>
                </div>
                
                {/* Tab Content - Full Width */}
                <div className="tab-content-full">
                    {activeTab === 'addQuestions' ? (
                        /* Add Questions Tab */
                        <div className="add-questions-tab">
                            <div className="questions-header">
                                <h3>إضافة أسئلة جديدة</h3>
                                <button className="add-question-btn" onClick={addQuestion}>
                                    <FaPlus />
                                    إضافة سؤال
                                </button>
                            </div>
                            
                            {addedQuestions.map((question, qIndex) => (
                                <div key={qIndex} className="question-item">
                                    <div className="question-header">
                                        <h4>السؤال {qIndex + 1}</h4>
                                        <button 
                                            className="remove-question-btn"
                                            onClick={() => removeQuestion(qIndex)}
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                    
                                    <div className="question-content">
                                        <div className="form-group">
                                            <label>نص السؤال</label>
                                            <textarea
                                                value={question.text}
                                                onChange={(e) => handleQuestionTextChange(qIndex, e.target.value)}
                                                placeholder="أدخل نص السؤال"
                                                rows="3"
                                            />
                                        </div>
                                        
                                        <div className="form-group">
                                            <label>صورة السؤال (اختياري)</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleQuestionImageChange(qIndex, e.target.files[0])}
                                            />
                                            {question.image && (
                                                <div className="image-preview">
                                                    <img src={URL.createObjectURL(question.image)} alt="Question" />
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="answers-section">
                                            <div className="answers-header">
                                                <h5>الإجابات</h5>
                                                <button 
                                                    className="add-answer-btn"
                                                    onClick={() => addAnswer(qIndex)}
                                                >
                                                    <FaPlus />
                                                    إضافة إجابة
                                                </button>
                                            </div>
                                            
                                            {question.answers.map((answer, aIndex) => (
                                                <div key={aIndex} className="answer-item">
                                                    <div className="answer-content">
                                                        <div className="answer-letter">
                                                            {getAnswerLetter(aIndex)}
                                                        </div>
                                                        
                                                        <div className="answer-inputs">
                                                            <input
                                                                type="text"
                                                                value={answer.text}
                                                                onChange={(e) => handleAnswerTextChange(qIndex, aIndex, e.target.value)}
                                                                placeholder="نص الإجابة"
                                                            />
                                                            
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={(e) => handleAnswerImageChange(qIndex, aIndex, e.target.files[0])}
                                                                placeholder="صورة الإجابة"
                                                            />
                                                            
                                                            {answer.image && (
                                                                <div className="image-preview small">
                                                                    <img src={URL.createObjectURL(answer.image)} alt="Answer" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        <div className="answer-actions">
                                                            <label className="correct-answer">
                                                                <input
                                                                    type="radio"
                                                                    name={`correct-${qIndex}`}
                                                                    checked={answer.true_status}
                                                                    onChange={() => handleCorrectAnswerChange(qIndex, aIndex)}
                                                                />
                                                                الإجابة الصحيحة
                                                            </label>
                                                            
                                                            {question.answers.length > 2 && (
                                                                <button 
                                                                    className="remove-answer-btn"
                                                                    onClick={() => removeAnswer(qIndex, aIndex)}
                                                                >
                                                                    <FaTrash />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Question Bank Tab */
                        <div className="question-bank-tab">
                            <div className="question-bank-header">
                                <h3>بنك الأسئلة - {availableSubjects.find(s => s.id == selectedSubjectId)?.title}</h3>
                                <p>اختر الأسئلة التي تريد إضافتها للواجب</p>
                            </div>
                            
                            {questionBankLoading ? (
                                <div className="loading">جاري تحميل الأسئلة...</div>
                            ) : (
                                <div className="question-bank-grid">
                                    {questionBank.map((question) => (
                                        <div 
                                            key={question.id} 
                                            className={`question-bank-card ${selectedQuestions.find(q => q.question_id === question.id) ? 'selected' : ''}`}
                                        >
                                            <div className="question-bank-content">
                                                <h4>{question.title}</h4>
                                                <div className="question-meta">
                                                    <span>عدد الإجابات: {question.answers?.length || 0}</span>
                                                    <span>النوع: سؤال اختيار من متعدد</span>
                                                </div>
                                                
                                                {/* Show answers preview */}
                                                {question.answers && question.answers.length > 0 && (
                                                    <div className="answers-preview">
                                                        <h5>الإجابات:</h5>
                                                        <div className="answers-list">
                                                            {question.answers.map((answer, index) => (
                                                                <div 
                                                                    key={answer.id} 
                                                                    className={`answer-preview ${answer.true_status ? 'correct' : ''}`}
                                                                >
                                                                    <span className="answer-letter">{String.fromCharCode(65 + index)}</span>
                                                                    <span className="answer-text">{answer.answer}</span>
                                                                    {answer.true_status && <span className="correct-mark">✓</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="question-selection">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedQuestions.find(q => q.question_id === question.id) ? true : false}
                                                    onChange={() => handleQuestionSelection(question.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Submit Button */}
                <div className="submit-section">
                    <button 
                        className="submit-btn mx-auto"
                        onClick={handleSubmit}
                        disabled={!selectedSubjectId || isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="loading-spinner"></div>
                                جاري الإنشاء...
                            </>
                        ) : (
                            <>
                                <FaSave />
                                إنشاء الواجب
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreateDuty;
